import React, { useState } from "react";
import { observer } from "mobx-react";
import { useEventListener } from "usehooks-ts";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import { OrderSummaryCard } from "../Summary/OrderSummaryCard";
import { api } from "@/api";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { OrderCard } from "../_shared/OrderCard";
import { OrderCardSummaryItem } from "../_shared/OrderCard/OrderCardSummaryItem";
import { H3 } from "@/components/Heading";
import { orderReviewAmount } from "@/constants/order";
import type { OrderDetails } from "@/store/types/order";
import { OrderPurchaseType } from "@/store/types/order";
import { ChaseIFrame } from "./ChaseIFrame";
import { useApiMutate } from "@/api/hooks/useApiMutate";
import { appEnv } from "@/constants/env";
import { getApiErrorMessage } from "@/api/getApiErrorMessage";
import { useCaptchaToken } from "@/components/Captcha/useCaptchaToken";
import { telemetry } from "@/telemetry";

const getChaseStatusMessage = (content: any) =>
  (Array.isArray(content) ? content : [content])
    .map((error) => error?.status_message)
    .find(
      (message): message is string =>
        typeof message === "string" && message.trim() !== "",
    );

const getOrigin = (url: string) => {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

export const OrderPaymentPanel = observer(() => {
  const {
    activeLanguage,
    orderData,
    setOrderData,
    activeIndigenousCommunity,
    isIndigenousFlow,
    orderCreatedData,
    setOrderCreatedData,
    setOrderValidatedData,
    orderTotals,
    chaseUrl,
    isEmergentFlow,
    ga4Store,
    telemetryStore,
    shippingProvinceLabel,
  } = useStore();
  const [isLoading, setLoading] = useState(false);
  const [orderRef, setOrderRef] = useState("");
  const language = translations.pages["order-summary"][activeLanguage];
  const isAccountOrder = orderData.purchaseType === OrderPurchaseType.Account;

  const navigate = useAppNavigate();

  const paymentProperties = () => ({
    orderId: orderCreatedData?.orderId,
    uid: orderCreatedData?.externalId,
    language: activeLanguage,
    total: orderTotals.total,
  });

  const trackValidateOrderFailed = (message: string, status?: number) =>
    telemetry.trackEvent("validate_order_failed", {
      orderId: orderCreatedData?.orderId,
      uid: orderCreatedData?.externalId,
      message,
      status,
    });

  const apiValidateOrder = useApiMutate({
    mutationFn: api.validateOrder,
    onSuccess: (data) => {
      if (appEnv.isDevelopment) data.success = true;

      setOrderValidatedData(data);

      if (data.success) {
        ga4Store.purchase(data.orderId);

        const isOver45k =
          orderTotals.total >= orderReviewAmount && !isIndigenousFlow;
        navigate({
          to: isOver45k ? "/order-request" : "/order-confirm",
        });
      } else {
        trackValidateOrderFailed(data.message);
        toast.error(getApiErrorMessage(null));
        setLoading(false);
      }
    },
    onError: (data) => {
      trackValidateOrderFailed(getApiErrorMessage(data), data.response?.status);
      toast.error(getApiErrorMessage(data));
      setLoading(false);
    },
  });

  const validateOrder = () => {
    setLoading(true);
    apiValidateOrder.mutate(orderCreatedData?.orderId || "");
  };

  const chaseOrigin = getOrigin(chaseUrl);

  const onMessage = (e: MessageEvent) => {
    if (!chaseOrigin || e.origin !== chaseOrigin) return;

    const type = e.data?.subject;

    if (type === "handleTransactionErrors") {
      const properties = { ...paymentProperties(), content: e.data.content };
      telemetry.trackEvent("chase_handleTransactionErrors", properties);
      telemetry.trackException(
        new Error("chase_handleTransactionErrors"),
        properties,
      );
      toast.error(
        getChaseStatusMessage(e.data.content) ?? getApiErrorMessage(null),
      );
      setLoading(false);
    } else if (type === "startPayment") {
      telemetry.trackEvent("chase_startPayment", paymentProperties());
      setLoading(true);
    } else if (type === "cancelPayment") {
      telemetry.trackEvent("chase_cancelPayment", {
        ...paymentProperties(),
        content: e.data.content,
      });
      setLoading(false);
    } else if (type === "completePayment") {
      telemetry.trackEvent("chase_completePayment", paymentProperties());
      validateOrder();
    }
  };

  useEventListener("message", onMessage);

  const getCaptchaToken = useCaptchaToken("create_account_order");

  const apiCreateAccountOrder = useApiMutate({
    mutationFn: async (data: OrderDetails) => {
      const captchaToken = await getCaptchaToken();
      return api.createOrder({ ...data, captchaToken });
    },
    onSuccess: (data) => {
      setOrderData({
        ...orderData,
        orderReference: orderRef,
      });
      setOrderCreatedData(data);
      telemetryStore.orderCreated(data.orderId, OrderPurchaseType.Account);
      ga4Store.purchase(data.orderId);
      navigate({ to: "/order-confirm" });
    },
    onError: (data) => {
      telemetryStore.orderCreateFailed(data, OrderPurchaseType.Account);
      toast.error(getApiErrorMessage(data));
    },
  });

  const submitAccountOrder = () => {
    apiCreateAccountOrder.mutate({
      ...orderData,
      orderReference: orderRef,
    });
  };

  return (
    <HStack
      spacing="8"
      alignItems="flex-start"
      flexDir={{ base: "column", md: "row" }}
    >
      <Stack
        flex="1"
        spacing="8"
      >
        <H3>
          {isAccountOrder
            ? language.pay_on_account_label
            : language.enter_payment_info}
        </H3>
        <Stack
          position="relative"
          width="100%"
        >
          {isLoading && (
            <Box
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex="1"
              width="100%"
              height="100%"
              bg="rgba(0,0,0,0.5)"
            >
              <Spinner size="xl" />
            </Box>
          )}

          {isAccountOrder ? (
            <>
              <Stack maxWidth="xl">
                <Text>{language.enter_order_reference_number_2}</Text>
                <HStack spacing="8">
                  <Input
                    autoFocus
                    value={orderRef}
                    onChange={(e) => setOrderRef(e.target.value)}
                    placeholder={language.order_reference_number}
                  />
                  <Button
                    onClick={submitAccountOrder}
                    isLoading={apiCreateAccountOrder.isPending}
                    isDisabled={!orderRef}
                  >
                    {language.submit_order}
                  </Button>
                </HStack>
              </Stack>
            </>
          ) : (
            <>
              {appEnv.isDevelopment ? (
                <Button onClick={validateOrder}>Complete Payment</Button>
              ) : (
                <ChaseIFrame url={chaseUrl} />
              )}
            </>
          )}
        </Stack>
      </Stack>
      <Stack
        spacing="8"
        maxWidth="100%"
      >
        <OrderSummaryCard isFinalCost />

        {orderData.ada && (
          <OrderCard
            title={
              isIndigenousFlow
                ? language.emergent_ada
                : language.emergent_direct_account_ada
            }
          >
            <Text>{orderData.ada}</Text>
          </OrderCard>
        )}

        <OrderCard title={language.shipping_address}>
          <OrderCardSummaryItem
            label={
              isIndigenousFlow
                ? language.community_name
                : language.shipping_name
            }
            value={
              isIndigenousFlow
                ? activeIndigenousCommunity?.name ?? ""
                : orderData.shippingName
            }
          />
          {!isIndigenousFlow && (
            <OrderCardSummaryItem
              label={language.shipping_business_name}
              value={orderData.shippingBusinessName}
            />
          )}
          <OrderCardSummaryItem
            label={language.shipping_street_address}
            value={orderData.shippingStreetAddress}
          />
          <OrderCardSummaryItem
            label={language.shipping_unit_suit}
            value={orderData.shippingOptionalUnit}
          />
          <OrderCardSummaryItem
            label={language.shipping_city}
            value={orderData.shippingCity}
          />
          <OrderCardSummaryItem
            label={
              isEmergentFlow ? language.province_territory : language.province
            }
            value={shippingProvinceLabel}
          />
          <OrderCardSummaryItem
            label={language.shipping_postal_code}
            value={orderData.shippingPostalCode}
          />
        </OrderCard>

        <OrderCard title={language.contact_information}>
          {isIndigenousFlow && (
            <OrderCardSummaryItem
              label={language.contact_name}
              value={orderData.shippingName}
            />
          )}
          <OrderCardSummaryItem
            label={language.phone_no}
            value={orderData.phoneNumber}
          />
          <OrderCardSummaryItem
            label={language.email_address}
            value={orderData.emailAddress}
          />
        </OrderCard>

        {orderData.orderReference && (
          <OrderCard title={language.order_reference_number}>
            <Text>{orderData.orderReference}</Text>
          </OrderCard>
        )}
      </Stack>
    </HStack>
  );
});
